const GAME_CONTAINER_ID = 'game-container';
const FS_TOGGLE_ID = 'fs-toggle';
const PSEUDO_FS_CLASS = 'game-fs-pseudo';

export class FullscreenSystem {
  private static wired = false;

  static isSupported(): boolean {
    const el = FullscreenSystem.getTargetElement();
    if (!el) return true;
    return document.fullscreenEnabled !== false
      || !!(el as HTMLElement & { requestFullscreen?: () => Promise<void> }).requestFullscreen
      || !!(el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen
      || !!(document.documentElement as HTMLElement & { requestFullscreen?: () => Promise<void> }).requestFullscreen;
  }

  static isNativeActive(): boolean {
    const el = FullscreenSystem.getTargetElement();
    return !!(
      document.fullscreenElement === el
      || document.fullscreenElement === document.documentElement
      || (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement
    );
  }

  static isPseudoActive(): boolean {
    return document.body.classList.contains(PSEUDO_FS_CLASS);
  }

  static isActive(): boolean {
    return FullscreenSystem.isNativeActive() || FullscreenSystem.isPseudoActive();
  }

  private static getTargetElement(): HTMLElement | null {
    return document.getElementById(GAME_CONTAINER_ID);
  }

  static async enter(): Promise<boolean> {
    const targets: HTMLElement[] = [];
    const container = FullscreenSystem.getTargetElement();
    if (container) targets.push(container);
    targets.push(document.documentElement);

    for (const target of targets) {
      try {
        const el = target as HTMLElement & {
          requestFullscreen?: () => Promise<void>;
          webkitRequestFullscreen?: () => Promise<void>;
        };
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        } else {
          continue;
        }
        if (FullscreenSystem.isNativeActive()) {
          document.body.classList.remove(PSEUDO_FS_CLASS);
          return true;
        }
      } catch {
        // Coba target berikutnya
      }
    }

    document.body.classList.add(PSEUDO_FS_CLASS);
    return true;
  }

  static async exit(): Promise<boolean> {
    document.body.classList.remove(PSEUDO_FS_CLASS);

    try {
      const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> };
      if (document.fullscreenElement) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
      }
      return !FullscreenSystem.isActive();
    } catch {
      return !FullscreenSystem.isActive();
    }
  }

  static async toggle(): Promise<boolean> {
    if (FullscreenSystem.isActive()) {
      await FullscreenSystem.exit();
      return false;
    }
    return FullscreenSystem.enter();
  }

  static bindChange(handler: () => void): () => void {
    const onChange = () => handler();
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }

  /** Pastikan tombol fullscreen tetap ada, di dalam container, dan terlihat (termasuk native fullscreen). */
  static ensureToggleVisible(): void {
    const btn = document.getElementById(FS_TOGGLE_ID);
    if (!btn) return;
    const mount = FullscreenSystem.getOverlayMount();
    if (btn.parentElement !== mount) {
      mount.appendChild(btn);
    }
    btn.hidden = false;
    btn.style.display = 'flex';
    btn.style.visibility = 'visible';
    btn.style.opacity = '1';
    btn.style.pointerEvents = 'auto';
    btn.style.zIndex = '10050';
  }

  static getContainerElement(): HTMLElement | null {
    return FullscreenSystem.getTargetElement();
  }

  /** Mount point untuk overlay HTML agar tetap tampil saat native fullscreen (#game-container). */
  static getOverlayMount(): HTMLElement {
    return FullscreenSystem.getTargetElement() ?? document.body;
  }

  /** Pasang handler pada tombol #fs-toggle — selalu ada di HTML */
  static mountToggleButton(): () => void {
    if (FullscreenSystem.wired) return () => undefined;

    const btn = document.getElementById(FS_TOGGLE_ID) as HTMLButtonElement | null;
    if (!btn) return () => undefined;

    FullscreenSystem.wired = true;
    FullscreenSystem.ensureToggleVisible();

    const sync = () => {
      FullscreenSystem.ensureToggleVisible();
      const active = FullscreenSystem.isActive();
      btn.classList.toggle('fs-active', active);
      btn.title = active ? 'Perkecil layar' : 'Perbesar layar';
      btn.setAttribute('aria-label', active ? 'Perkecil layar' : 'Perbesar layar');
    };

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await FullscreenSystem.toggle();
      sync();
    });

    sync();
    const unbind = FullscreenSystem.bindChange(sync);
    return () => {
      unbind();
      FullscreenSystem.wired = false;
    };
  }
}
